/**
 * UnterschriftenPage - Administration > Dokumente & Unterschriften > Ähnlichkeitssuche (P4-S5, Kap. 7.10).
 *
 * The customer-adjustable Unterschriften-Ähnlichkeitsschwellen: the "Schwellwert automatische Annahme"
 * and the "Untergrenze unsicher". These REUSE the existing confidence_policy['mandate_signing'] row
 * (the same thresholds MandateControlService reads + snapshots on every Unterschriftenkontrolle) - no
 * parallel store. The generic Workflows > Routing section edits the raw thresholds; this screen is the
 * signature-domain surface Stefan asked for (sliders + the bestanden/unsicher/Befund legend).
 *
 * auto_threshold  = Schwellwert automatische Annahme (Score >= Wert: bestanden)
 * freigabe_threshold = Untergrenze unsicher (Untergrenze <= Score < Schwellwert: unsicher; darunter: Befund)
 *
 * Read-gated by routing_policy.read; the save is gated by routing_policy.manage (the api PUT
 * /admin/confidence-policy/:process is the hard line). Changes are audited (UPDATE / confidence_policy).
 *
 * [Source: apps/web/src/features/administration/pages/WorkflowsPage.tsx] (confidence-policy edit pattern)
 * [Source: apps/web/src/features/administration/pages/ProzessErinnerungenPage.tsx] (configurable-Fristen screen)
 */

import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  NEW_REFERENCE_ALWAYS_CONFIRM,
  OPTICAL_CHECK_ON_DIGITAL_SIGNATURE,
  SIGNATURE_THRESHOLD_BOUNDS,
  checkConfidencePolicyBounds,
} from '@brokly/shared';
import { apiClient } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { Badge } from '../../../components/ui/Badge';
import { AdminPageHeader } from '../AdminPageHeader';
import { ADMIN_PAGE_META } from '../adminNav';
import { useConfidencePolicy } from '../hooks';
import { useEffectivePermissions } from '../useEffectivePermissions';
import { Button, LoadingState, ErrorState, TextInput } from '../components';

const meta = ADMIN_PAGE_META['unterschriften'];

// Kap. 7.10 Bandbreite (0.70-0.99 / 0.50-Schwellwert) = SIGNATURE_THRESHOLD_BOUNDS from @brokly/shared.
// The api enforces the same band on every input path; the pre-check here only saves a round
// trip and, for a band violation, shows the api's message.

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Typed text to a number; a Swiss decimal comma counts as a point. NaN fails the band check. */
function toNumber(s: string): number {
  return Number(s.replace(',', '.'));
}

function SimilaritySettings({ auto: auto0, lower: lower0, isDefault, canManage }: {
  auto: number;
  lower: number;
  isDefault: boolean;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  // Kept as typed text (like the Workflows routing rows), so '0.' and '0,85' can be entered; parsed on save.
  const [auto, setAuto] = useState(String(auto0));
  const [lower, setLower] = useState(String(lower0));
  const autoNum = toNumber(auto);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const a = round2(autoNum);
      const l = round2(toNumber(lower));
      const violation = checkConfidencePolicyBounds('mandate_signing', a, l);
      if (violation) throw new Error(violation.message);
      return apiClient.admin.setConfidencePolicy('mandate_signing', {
        auto_threshold: a,
        freigabe_threshold: l,
      });
    },
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.adminConfidencePolicy() });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div data-testid="signature-similarity" className="space-y-6">
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="mb-3 flex items-center gap-2">
          <span className="text-sm font-semibold">Ähnlichkeitssuche</span>
          {isDefault && <Badge variant="neutral">Standard</Badge>}
        </div>

        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Schwellwert automatische Annahme
            </label>
            <input
              type="range"
              min={SIGNATURE_THRESHOLD_BOUNDS.auto.min}
              max={SIGNATURE_THRESHOLD_BOUNDS.auto.max}
              step={SIGNATURE_THRESHOLD_BOUNDS.step}
              value={auto}
              disabled={!canManage}
              onChange={(e) => setAuto(e.target.value)}
              aria-label="Schwellwert automatische Annahme (Schieberegler)"
              className="w-full"
            />
            <TextInput
              inputMode="decimal"
              value={auto}
              disabled={!canManage}
              onChange={(e) => setAuto(e.target.value)}
              aria-label="Schwellwert automatische Annahme"
              className="mt-2 tabular-nums"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Untergrenze unsicher
            </label>
            <input
              type="range"
              min={SIGNATURE_THRESHOLD_BOUNDS.lower.min}
              max={Number.isNaN(autoNum) ? SIGNATURE_THRESHOLD_BOUNDS.auto.max : autoNum}
              step={SIGNATURE_THRESHOLD_BOUNDS.step}
              value={lower}
              disabled={!canManage}
              onChange={(e) => setLower(e.target.value)}
              aria-label="Untergrenze unsicher (Schieberegler)"
              className="w-full"
            />
            <TextInput
              inputMode="decimal"
              value={lower}
              disabled={!canManage}
              onChange={(e) => setLower(e.target.value)}
              aria-label="Untergrenze unsicher"
              className="mt-2 tabular-nums"
            />
          </div>
        </div>

        <p data-testid="similarity-legend" className="mt-4 rounded-lg border border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
          Score &ge; Schwellwert: bestanden &middot; zwischen Untergrenze und Schwellwert: unsicher &middot; darunter: Befund.
        </p>

        <label className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={NEW_REFERENCE_ALWAYS_CONFIRM}
            disabled
            readOnly
            aria-label="Neue Referenz immer bestätigen"
          />
          Neue Referenz immer bestätigen (fix)
        </label>

        <label className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={OPTICAL_CHECK_ON_DIGITAL_SIGNATURE}
            disabled
            readOnly
            aria-label="Optische Prüfung bei digitaler Signatur"
          />
          Optische Prüfung bei digitaler Signatur (fix)
        </label>

        {error != null && <div className="mt-3 text-sm text-status-danger">{error}</div>}

        {canManage && (
          <div className="mt-4 flex justify-end">
            <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
              Speichern
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export function UnterschriftenPage() {
  const { can } = useEffectivePermissions();
  const canManage = can('routing_policy.manage');
  const { data, isLoading, isError } = useConfidencePolicy();

  const entry = data?.find((p) => p.process === 'mandate_signing');

  // Re-hydrate the uncontrolled form when the server data changes (ProzessErinnerungen idiom).
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (entry != null) setVersion((v) => v + 1);
  }, [entry]);

  return (
    <div>
      <AdminPageHeader title={meta.title} description={meta.description} />

      {isLoading ? (
        <LoadingState />
      ) : isError || entry == null ? (
        <ErrorState />
      ) : (
        <SimilaritySettings
          key={`sim-${version}`}
          auto={Number(entry.auto_threshold)}
          lower={Number(entry.freigabe_threshold)}
          isDefault={entry.is_default}
          canManage={canManage}
        />
      )}
    </div>
  );
}
